import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  Container,
  FormErrorMessage,
  Link,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '../../services/api';

const ForgotPassword = () => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSent, setIsSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Email is required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await api.forgotPassword(email);
      setIsSent(true);
      toast({
        title: 'Email Sent',
        description: 'Check your email for password reset instructions.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email');
      toast({
        title: 'Error',
        description: err.message || 'Failed to send reset email',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg="white" display="flex" alignItems="center" py={8}>
      <Container maxW="420px" w="full">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            bg="white"
            border="1px solid"
            borderColor="neutral.200"
            borderRadius="12px"
            p={8}
            boxShadow="0 2px 8px rgba(0, 0, 0, 0.05)"
          >
            <VStack spacing={6} align="stretch">
              <VStack spacing={2} align="start">
                <Heading as="h1" fontSize="28px" fontWeight="700" color="neutral.900">
                  Reset Password
                </Heading>
                <Text fontSize="14px" color="neutral.600" lineHeight="1.5">
                  Enter your email address and we'll send you instructions to reset your password.
                </Text>
              </VStack>

              {isSent ? (
                <VStack spacing={4}>
                  <Text color="green.600" fontWeight="600" textAlign="center">
                    Reset link has been sent to your email!
                  </Text>
                  <Button
                    as={RouterLink}
                    to="/login"
                    w="full"
                    h="40px"
                    bg="primary.500"
                    color="white"
                    _hover={{ bg: 'primary.600' }}
                  >
                    Return to Login
                  </Button>
                </VStack>
              ) : (
                <form onSubmit={handleSubmit}>
                  <VStack spacing={4}>
                    <FormControl isRequired isInvalid={!!error}>
                      <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                        Email Address
                      </FormLabel>
                      <Input
                        type="email"
                        placeholder="name@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        borderColor="neutral.200"
                        _focus={{
                          borderColor: 'primary.500',
                          boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                        }}
                        fontSize="14px"
                        h="40px"
                      />
                      {error && (
                        <FormErrorMessage fontSize="12px">{error}</FormErrorMessage>
                      )}
                    </FormControl>

                    <Button
                      type="submit"
                      w="full"
                      h="40px"
                      bg="primary.500"
                      color="white"
                      fontWeight="600"
                      fontSize="14px"
                      borderRadius="8px"
                      _hover={{
                        bg: 'primary.600',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(26, 94, 63, 0.3)',
                      }}
                      _active={{
                        transform: 'translateY(0)',
                        boxShadow: '0 2px 4px rgba(26, 94, 63, 0.2)',
                      }}
                      isLoading={isLoading}
                      mt={2}
                    >
                      Send Reset Link
                    </Button>
                  </VStack>
                </form>
              )}

              <Box h="1px" bg="neutral.200" w="full" />

              <VStack spacing={2} align="center">
                <Text fontSize="14px" color="neutral.600">
                  Remember your password?{' '}
                  <Link
                    as={RouterLink}
                    to="/login"
                    color="primary.500"
                    fontWeight="600"
                    _hover={{ color: 'primary.600', textDecoration: 'none' }}
                  >
                    Sign in
                  </Link>
                </Text>
              </VStack>
            </VStack>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
